/**
 * Einkauf: alles, was sich beschaffen lässt, an einem Ort.
 *
 * Gekauft wurde bisher an sieben Stellen – Server im Rechenzentrum, Router im
 * Netz, Firewall im Betrieb, Balancer auf der Balancerseite, Ersatzteile in der
 * Beschaffung, Racks im Standort, IP-Blöcke in der Adressverwaltung. Jede
 * einzelne Stelle ist dort richtig aufgehoben, weil man dort steht, wenn einem
 * etwas fehlt. Nur die umgekehrte Frage – "was kann ich überhaupt kaufen, und
 * was kostet es?" – hatte keinen Ort.
 *
 * Diese Seite ist deshalb bewusst ein *zweiter* Weg und kein Ersatz: Die
 * bisherigen Knöpfe bleiben, wo sie sind. Sie fügt keine Mechanik hinzu,
 * sondern führt dieselben Kommandos an einer Stelle zusammen, an der sich
 * vergleichen lässt.
 */
import { useMemo, useState } from 'react';
import type { ServerRole } from '@hosting-sim/core';
import {
  Badge,
  Button,
  Card,
  Column,
  DataTable,
  EmptyState,
  Field,
  Input,
  Modal,
  Notice,
  PageHeader,
  Select,
  Stat,
  StatGrid,
  Tabs,
  type Tone,
} from '../components/ui';
import { useGame, useMetrics } from '../store/game';
import { useCatalog } from './Infrastructure';
import { ServerConfiguratorDialog } from '../components/ServerConfigurator';
import { bytes, dec, duration, mbps, money, num, watt } from '../lib/format';

export type DeviceKind = 'server' | 'router' | 'switch' | 'firewall' | 'loadbalancer' | 'ups';

export const KIND_LABEL: Record<DeviceKind, string> = {
  server: 'Server',
  router: 'Router',
  switch: 'Switch',
  firewall: 'Firewall',
  loadbalancer: 'Balancer',
  ups: 'USV',
};

const TIER_TONE: Record<string, Tone> = { budget: 'muted', standard: 'neutral', enterprise: 'info' };

const ROLE_LABEL: Record<Exclude<ServerRole, 'unassigned'>, string> = {
  'shared-hosting': 'Shared Hosting',
  'vps-host': 'VPS-Host',
  dedicated: 'Dedicated',
  storage: 'Storage',
  mail: 'Mail',
  dns: 'DNS',
  backup: 'Backup',
  'gpu-host': 'GPU-Host',
};

/** Eine Zeile im Regal – aus allen Gerätearten auf eine Form gebracht. */
export interface ShelfItem {
  kind: DeviceKind;
  key: string;
  name: string;
  description: string;
  vendorTier: string;
  price: number;
  maintenancePerMonth: number;
  heightUnits: number;
  maxWatt: number;
  /** Die zwei bis drei Zahlen, die diese Geräteart ausmachen. */
  specs: string;
  /**
   * Zentrale USV gehören dem Standort, nicht einem Rack.
   *
   * Sie belegen deshalb keine Höheneinheit und brauchen im Kaufdialog eine
   * Standort- statt einer Rack-Auswahl.
   */
  central?: boolean;
}

/**
 * Die Eckdaten je Geräteart.
 *
 * Bewusst nicht dieselben Spalten für alle: Bei einem Server zählen Kerne und
 * Speicher, bei einem Switch Portzahl und Backplane, bei einer USV Kilowatt und
 * Laufzeit. Eine gemeinsame Tabelle mit lauter leeren Zellen wäre ordentlicher
 * und weniger brauchbar.
 */
export function shelf(catalog: NonNullable<ReturnType<typeof useCatalog>>): ShelfItem[] {
  const base = (kind: DeviceKind, m: Record<string, never>): Omit<ShelfItem, 'specs'> => ({
    kind,
    key: m.key as unknown as string,
    name: m.name as unknown as string,
    description: m.description as unknown as string,
    vendorTier: (m.vendorTier as unknown as string) ?? 'standard',
    price: m.price as unknown as number,
    maintenancePerMonth: (m.maintenancePerMonth as unknown as number) ?? 0,
    heightUnits: (m.heightUnits as unknown as number) ?? 0,
    maxWatt: (m.maxWatt as unknown as number) ?? 0,
    central: (m.central as unknown as boolean) ?? false,
  });

  const items: ShelfItem[] = [];

  for (const m of catalog.servers as never[]) {
    const s = m as unknown as { cpuCores: number; cpuGhz: number; ramGb: number; ssdGb: number; hddGb: number; nicMbps: number };
    items.push({
      ...base('server', m),
      specs: `${s.cpuCores} Kerne · ${dec(s.cpuGhz, 1)} GHz · ${bytes(s.ramGb)} RAM · ${bytes(s.ssdGb + s.hddGb)} · ${mbps(s.nicMbps)}`,
    });
  }
  for (const m of catalog.routers as never[]) {
    const r = m as unknown as { portCount: number; portSpeedMbps: number; forwardingCapacityMbps: number; fibCapacity: number; maxBgpSessions: number };
    items.push({
      ...base('router', m),
      specs: `${r.portCount}×${mbps(r.portSpeedMbps)} · ${mbps(r.forwardingCapacityMbps)} Durchsatz · FIB ${num(r.fibCapacity)} · ${r.maxBgpSessions} Sitzungen`,
    });
  }
  for (const m of catalog.switches as never[]) {
    const s = m as unknown as { portCount: number; portSpeedMbps: number; uplinkCount: number; uplinkSpeedMbps: number; switchingCapacityMbps: number; layer3: boolean };
    items.push({
      ...base('switch', m),
      specs:
        `${s.portCount}×${mbps(s.portSpeedMbps)}` +
        (s.uplinkCount > 0 ? ` + ${s.uplinkCount}×${mbps(s.uplinkSpeedMbps)} Uplink` : '') +
        ` · ${mbps(s.switchingCapacityMbps)} Backplane` +
        (s.layer3 ? ' · Layer 3' : ''),
    });
  }
  for (const m of catalog.firewalls as never[]) {
    const f = m as unknown as { throughputMbps: number; sessionCapacity: number; scrubbingGbps: number };
    items.push({
      ...base('firewall', m),
      specs:
        `${mbps(f.throughputMbps)} · ${num(f.sessionCapacity)} Sitzungen` +
        (f.scrubbingGbps > 0 ? ` · ${f.scrubbingGbps} Gbit/s Scrubbing` : ' · ohne Scrubbing'),
    });
  }
  for (const m of catalog.loadbalancers as never[]) {
    const l = m as unknown as { throughputMbps: number; connectionCapacity: number; tlsHandshakesPerSecond: number };
    items.push({
      ...base('loadbalancer', m),
      specs: `${mbps(l.throughputMbps)} · ${num(l.connectionCapacity)} Verbindungen · ${num(l.tlsHandshakesPerSecond)} TLS/s`,
    });
  }
  for (const m of catalog.ups as never[]) {
    const u = m as unknown as { capacityKwh: number; outputKw: number };
    items.push({ ...base('ups', m), specs: `${dec(u.capacityKwh, 1)} kWh · ${dec(u.outputKw, 1)} kW Abgabe` });
  }

  return items;
}

export function Shop() {
  const world = useGame((s) => s.world);
  const metrics = useMetrics();
  const catalog = useCatalog();
  const [tab, setTab] = useState('hardware');
  const [configuring, setConfiguring] = useState(false);

  if (!world || !metrics) return null;

  return (
    <>
      <PageHeader
        title="Einkauf"
        description="Der vollständige Katalog – Geräte, Racks, Ersatzteile und Adressen."
        actions={
          <Button onClick={() => setConfiguring(true)}>Server konfigurieren</Button>
        }
      />

      <StatGrid cols={4}>
        <Stat label="Liquidität" value={money(metrics.company.cash)} hint="was heute zur Verfügung steht" />
        <Stat
          label="Freie Höheneinheiten"
          value={num(metrics.derived.capacity.rackUnitsTotal - metrics.derived.capacity.rackUnitsUsed)}
          hint={`von ${num(metrics.derived.capacity.rackUnitsTotal)} HE`}
          tone={metrics.derived.capacity.rackUnitsTotal - metrics.derived.capacity.rackUnitsUsed <= 2 ? 'warning' : 'neutral'}
        />
        <Stat
          label="Freie Anschlussleistung"
          value={`${dec(metrics.derived.capacity.powerKwTotal - metrics.derived.capacity.powerKwUsed, 1)} kW`}
          hint={`von ${dec(metrics.derived.capacity.powerKwTotal, 1)} kW`}
        />
        <Stat label="Monatliche Kosten" value={money(metrics.derived.finance.monthlyCost)} hint="was schon läuft" />
      </StatGrid>

      <div className="mt-4">
        <Tabs
          tabs={[
            { id: 'hardware', label: 'Geräte' },
            { id: 'racks', label: 'Racks' },
            { id: 'parts', label: 'Ersatzteile' },
            { id: 'addresses', label: 'Adressen' },
          ]}
          active={tab}
          onChange={setTab}
        />
      </div>

      {tab === 'hardware' && <HardwareShelf catalog={catalog} />}
      {tab === 'racks' && <RackShelf catalog={catalog} />}
      {tab === 'parts' && <PartShelf catalog={catalog} />}
      {tab === 'addresses' && <AddressShelf />}

      <ServerConfiguratorDialog open={configuring} onClose={() => setConfiguring(false)} catalog={catalog} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Geräte
// ---------------------------------------------------------------------------

function HardwareShelf({ catalog }: { catalog: ReturnType<typeof useCatalog> }) {
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState<'all' | DeviceKind>('all');
  const [chosen, setChosen] = useState<ShelfItem | null>(null);

  const items = useMemo(() => (catalog ? shelf(catalog) : []), [catalog]);

  const visible = items
    .filter((item) => kind === 'all' || item.kind === kind)
    .filter((item) => {
      const needle = search.trim().toLowerCase();
      if (!needle) return true;
      return `${item.name} ${item.description} ${item.specs} ${KIND_LABEL[item.kind]}`.toLowerCase().includes(needle);
    });

  const columns: Column<ShelfItem>[] = [
    {
      key: 'kind',
      header: 'Art',
      sortValue: (i) => KIND_LABEL[i.kind],
      render: (i) => <Badge tone="muted">{KIND_LABEL[i.kind]}</Badge>,
    },
    {
      key: 'name',
      header: 'Modell',
      sortValue: (i) => i.name,
      render: (i) => (
        <div className="min-w-0">
          <p className="flex items-center gap-2 font-medium">
            {i.name}
            <Badge tone={TIER_TONE[i.vendorTier] ?? 'neutral'}>{i.vendorTier}</Badge>
          </p>
          <p className="text-2xs text-panel-500">{i.description}</p>
        </div>
      ),
    },
    { key: 'specs', header: 'Eckdaten', hideOnMobile: true, render: (i) => <span className="text-xs text-panel-500">{i.specs}</span> },
    {
      key: 'platz',
      header: 'Platz & Strom',
      align: 'right',
      hideOnMobile: true,
      sortValue: (i) => i.heightUnits,
      render: (i) => (
        <span className="tabular text-xs text-panel-500">{i.central ? `Zuleitung · ${watt(i.maxWatt)}` : `${i.heightUnits} HE · ${watt(i.maxWatt)}`}</span>
      ),
    },
    {
      key: 'price',
      header: 'Preis',
      align: 'right',
      sortValue: (i) => i.price,
      render: (i) => (
        <div>
          <p className="tabular text-sm font-medium">{money(i.price)}</p>
          {i.maintenancePerMonth > 0 && <p className="tabular text-2xs text-panel-500">+ {money(i.maintenancePerMonth)}/Mon.</p>}
        </div>
      ),
    },
    {
      key: 'buy',
      header: '',
      width: '6rem',
      render: (i) => (
        <Button size="sm" variant="primary" onClick={() => setChosen(i)}>
          Kaufen
        </Button>
      ),
    },
  ];

  if (!catalog) return <Card className="mt-4">Katalog wird geladen …</Card>;

  return (
    <>
      <div className="mt-4 flex flex-wrap items-end gap-3">
        <Input placeholder="Suchen …" value={search} onChange={(e) => setSearch(e.target.value)} className="w-56" />
        <Select label="Art" value={kind} onChange={(e) => setKind(e.target.value as 'all' | DeviceKind)}>
          <option value="all">Alle Geräte</option>
          {Object.entries(KIND_LABEL).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
        <p className="pb-2 text-2xs text-panel-500">
          {visible.length} von {items.length} Modellen. Weitere schaltet die Forschung frei.
        </p>
      </div>

      <Card className="mt-3" padded={false}>
        <DataTable columns={columns} rows={visible} rowKey={(i) => `${i.kind}-${i.key}`} empty="Kein Modell passt zur Suche." />
      </Card>

      {chosen && <BuyDialog item={chosen} onClose={() => setChosen(null)} />}
    </>
  );
}

/**
 * Kaufabschluss: Einbauort wählen, bei Servern zusätzlich die Rolle.
 *
 * Der Einbauort ist keine Formsache – ein Gerät ohne Rack existiert im Spiel
 * nicht. Deshalb steht hier auch, was danach noch fehlt: Ein Server braucht ein
 * Betriebssystem und ein Kabel, bevor er einen Kunden sieht.
 */
function BuyDialog({ item, onClose }: { item: ShelfItem; onClose: () => void }) {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  const [rackId, setRackId] = useState('');
  const [datacenterId, setDatacenterId] = useState('');
  const [role, setRole] = useState<ServerRole>('shared-hosting');
  const [busy, setBusy] = useState(false);

  if (!world) return null;
  const racks = Object.values(world.racks);
  const datacenters = Object.values(world.datacenters);
  /*
   * Eine zentrale USV zieht in den Standort ein, nicht in ein Rack – sie
   * hängt als Zuleitung davor, nicht in einer bestimmten Höheneinheit.
   * Deshalb fragt der Dialog hier nach dem Standort statt nach dem Rack.
   */
  const noTarget = item.central ? datacenters.length === 0 : racks.length === 0;
  const rackTarget = rackId || racks[0]?.id || '';
  const dcTarget = datacenterId || datacenters[0]?.id || '';

  async function buy() {
    setBusy(true);
    try {
      const rack = rackTarget as never;
      const dc = dcTarget as never;
      const result = await run(
        item.kind === 'server'
          ? { type: 'hw/buyServer', rackId: rack, modelKey: item.key, role }
          : item.kind === 'router'
            ? { type: 'hw/buyRouter', rackId: rack, modelKey: item.key }
            : item.kind === 'switch'
              ? { type: 'hw/buySwitch', rackId: rack, modelKey: item.key }
              : item.kind === 'firewall'
                ? { type: 'hw/buyFirewall', rackId: rack, modelKey: item.key }
                : item.kind === 'loadbalancer'
                  ? { type: 'hw/buyLoadBalancer', rackId: rack, modelKey: item.key }
                  : item.central
                    ? { type: 'hw/buyUps', datacenterId: dc, modelKey: item.key }
                    : { type: 'hw/buyUps', rackId: rack, modelKey: item.key },
      );
      if (result.ok) onClose();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`${item.name} kaufen`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button variant="primary" loading={busy} disabled={noTarget} onClick={buy}>
            Für {money(item.price)} kaufen
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Field label="Art">{KIND_LABEL[item.kind]}</Field>
          <Field label="Kaufpreis">{money(item.price)}</Field>
          <Field label="Wartung">{item.maintenancePerMonth > 0 ? `${money(item.maintenancePerMonth)}/Mon.` : '–'}</Field>
          <Field label="Platz & Strom">{item.central ? `Zuleitung · ${watt(item.maxWatt)}` : `${item.heightUnits} HE · ${watt(item.maxWatt)}`}</Field>
        </div>

        <p className="text-xs text-panel-500 dark:text-panel-400">{item.specs}</p>

        {noTarget ? (
          <Notice tone="warning" title={item.central ? 'Kein Standort vorhanden' : 'Kein Rack vorhanden'}>
            {item.central
              ? 'Eine zentrale USV braucht einen Standort. Unter „Rechenzentren" lässt sich einer eröffnen.'
              : 'Ein Gerät braucht einen Einbauort. Unter „Racks“ lässt sich eines anmieten.'}
          </Notice>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {item.central ? (
              <Select label="Standort" value={dcTarget} onChange={(e) => setDatacenterId(e.target.value)}>
                {datacenters.map((dc) => (
                  <option key={dc.id} value={dc.id}>
                    {dc.city} – {dc.name}
                  </option>
                ))}
              </Select>
            ) : (
              <Select label="Einbauort" value={rackTarget} onChange={(e) => setRackId(e.target.value)}>
                {racks.map((rack) => (
                  <option key={rack.id} value={rack.id}>
                    {world.datacenters[rack.datacenterId]?.city} – {rack.name}
                  </option>
                ))}
              </Select>
            )}
            {item.kind === 'server' && (
              <Select label="Rolle" value={role} onChange={(e) => setRole(e.target.value as ServerRole)}>
                {Object.entries(ROLE_LABEL).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            )}
          </div>
        )}

        {item.central && (
          <Notice tone="info" title="Keine Höheneinheit, kein Rackplatz">
            Diese USV steht neben den Racks und versorgt den ganzen Standort – sie taucht deshalb in keiner
            Bestückungsliste auf und lässt sich nicht in ein Rack umbauen.
          </Notice>
        )}

        {item.kind === 'server' && (
          <Notice tone="info" title="Danach fehlt noch das Kabel">
            Debian wird gleich mitinstalliert. Ans Netz kommt der Server erst, wenn er an einem Switch steckt – und der
            Switch an einem Router.
          </Notice>
        )}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Racks
// ---------------------------------------------------------------------------

function RackShelf({ catalog }: { catalog: ReturnType<typeof useCatalog> }) {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  const [datacenterId, setDatacenterId] = useState('');

  if (!world || !catalog) return <Card className="mt-4">Katalog wird geladen …</Card>;
  const datacenters = Object.values(world.datacenters);
  const target = datacenterId || datacenters[0]?.id || '';

  return (
    <>
      {datacenters.length === 0 ? (
        <Card className="mt-4">
          <EmptyState
            title="Noch kein Standort"
            description="Racks stehen in einem Rechenzentrum. Unter „Rechenzentren“ lässt sich einer eröffnen oder aushandeln."
          />
        </Card>
      ) : (
        <>
          <div className="mt-4 max-w-sm">
            <Select label="Standort" value={target} onChange={(e) => setDatacenterId(e.target.value)}>
              {datacenters.map((dc) => (
                <option key={dc.id} value={dc.id}>
                  {dc.city} – {dc.name}
                </option>
              ))}
            </Select>
          </div>

          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {(catalog.racks as never[]).map((entry) => {
              const rack = entry as unknown as {
                key: string;
                name: string;
                units: number;
                powerBudgetKw: number;
                monthlyCost: number;
                setupCost: number;
                description: string;
              };
              return (
                <Card key={rack.key} title={rack.name} subtitle={rack.description}>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Höhe">{rack.units} HE</Field>
                    <Field label="Strombudget">{dec(rack.powerBudgetKw, 1)} kW</Field>
                    <Field label="Einrichtung">{money(rack.setupCost)}</Field>
                    <Field label="Monatsmiete">{money(rack.monthlyCost)}</Field>
                  </div>
                  <Button
                    className="mt-3"
                    variant="primary"
                    size="sm"
                    onClick={() => void run({ type: 'rack/rent', datacenterId: target as never, modelKey: rack.key })}
                  >
                    Anmieten
                  </Button>
                </Card>
              );
            })}
          </div>
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Ersatzteile
// ---------------------------------------------------------------------------

function PartShelf({ catalog }: { catalog: ReturnType<typeof useCatalog> }) {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  const [datacenterId, setDatacenterId] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [express, setExpress] = useState(false);
  const [search, setSearch] = useState('');

  if (!world || !catalog) return <Card className="mt-4">Katalog wird geladen …</Card>;
  const datacenters = Object.values(world.datacenters);
  const target = datacenterId || datacenters[0]?.id || '';
  const menge = Math.max(1, Number(quantity) || 1);

  const parts = [
    ...(catalog.parts.cpu as never[]).map((p) => ({ p, art: 'Prozessor' })),
    ...(catalog.parts.ram as never[]).map((p) => ({ p, art: 'Speicher' })),
    ...(catalog.parts.drive as never[]).map((p) => ({ p, art: 'Laufwerk' })),
    ...(catalog.parts.nic as never[]).map((p) => ({ p, art: 'Netzwerkkarte' })),
  ].map(({ p, art }) => {
    const teil = p as unknown as { key: string; name: string; price: number; description?: string; leadTimeTicks: number };
    return { key: teil.key, name: teil.name, price: teil.price, description: teil.description ?? '', lead: teil.leadTimeTicks, art };
  });

  const visible = parts.filter((teil) => {
    const needle = search.trim().toLowerCase();
    return !needle || `${teil.name} ${teil.art} ${teil.description}`.toLowerCase().includes(needle);
  });

  const columns: Column<(typeof parts)[number]>[] = [
    { key: 'art', header: 'Art', sortValue: (t) => t.art, render: (t) => <Badge tone="muted">{t.art}</Badge> },
    {
      key: 'name',
      header: 'Bauteil',
      sortValue: (t) => t.name,
      render: (t) => (
        <div className="min-w-0">
          <p className="font-medium">{t.name}</p>
          {t.description && <p className="text-2xs text-panel-500">{t.description}</p>}
        </div>
      ),
    },
    {
      key: 'lead',
      header: 'Lieferzeit',
      align: 'right',
      hideOnMobile: true,
      sortValue: (t) => t.lead,
      render: (t) => <span className="tabular text-xs text-panel-500">{duration(t.lead)}</span>,
    },
    { key: 'price', header: 'Stückpreis', align: 'right', sortValue: (t) => t.price, render: (t) => <span className="tabular text-sm">{money(t.price)}</span> },
    {
      key: 'sum',
      header: `Summe (${menge}×)`,
      align: 'right',
      hideOnMobile: true,
      render: (t) => <span className="tabular text-sm font-medium">{money(t.price * menge)}</span>,
    },
    {
      key: 'order',
      header: '',
      width: '7rem',
      render: (t) => (
        <Button
          size="sm"
          variant="primary"
          disabled={datacenters.length === 0}
          onClick={() =>
            void run({ type: 'hw/orderParts', datacenterId: target as never, modelKey: t.key, quantity: menge, express })
          }
        >
          Bestellen
        </Button>
      ),
    },
  ];

  return (
    <>
      <div className="mt-4 flex flex-wrap items-end gap-3">
        <Input placeholder="Suchen …" value={search} onChange={(e) => setSearch(e.target.value)} className="w-48" />
        <Select label="Lieferort" value={target} onChange={(e) => setDatacenterId(e.target.value)}>
          {datacenters.map((dc) => (
            <option key={dc.id} value={dc.id}>
              {dc.city}
            </option>
          ))}
        </Select>
        <Input label="Menge" type="number" min={1} value={quantity} onChange={(e) => setQuantity(e.target.value)} className="w-24" />
        <label className="flex items-center gap-2 pb-2 text-xs">
          <input type="checkbox" checked={express} onChange={(e) => setExpress(e.target.checked)} />
          Express
        </label>
      </div>

      <Notice tone="info">
        Bauteile gehen ins Ersatzteillager des Standorts und werden von dort in einem Wartungsfenster verbaut. Der
        Lagerbestand steht unter „Ersatzteile“.
      </Notice>

      <Card className="mt-3" padded={false}>
        <DataTable columns={columns} rows={visible} rowKey={(t) => t.key} dense empty="Kein Bauteil passt zur Suche." />
      </Card>
    </>
  );
}

// ---------------------------------------------------------------------------
// Adressen
// ---------------------------------------------------------------------------

/**
 * IP-Blöcke kaufen.
 *
 * Sie stehen hier, weil sie im Wortsinn eingekauft werden – aber sie sind
 * keine Ware wie ein Server: Ein Block kostet monatlich, will angekündigt
 * werden und ist knapp. Deshalb die kurze Erklärung statt eines nackten
 * Preisschilds.
 */
function AddressShelf() {
  const run = useGame((s) => s.run);
  const [family, setFamily] = useState<'ipv4' | 'ipv6'>('ipv4');
  const [length, setLength] = useState('24');

  const optionen = family === 'ipv4' ? [22, 23, 24, 25, 26] : [32, 40, 44, 48];

  return (
    <>
      <div className="mt-4">
        <Notice tone="info">
          IPv4 ist erschöpft und wird gehandelt – der Preis richtet sich nach der Blockgröße und dem Weltmarkt. IPv6 gibt
          es dagegen praktisch umsonst. Gekaufte Blöcke müssen angekündigt werden, sonst erreicht sie niemand.
        </Notice>
      </div>

      <Card className="mt-3" title="Adressblock beschaffen">
        <div className="flex flex-wrap items-end gap-3">
          <Select label="Familie" value={family} onChange={(e) => setFamily(e.target.value as 'ipv4' | 'ipv6')}>
            <option value="ipv4">IPv4</option>
            <option value="ipv6">IPv6</option>
          </Select>
          <Select label="Größe" value={length} onChange={(e) => setLength(e.target.value)}>
            {optionen.map((bits) => (
              <option key={bits} value={bits}>
                /{bits}
                {family === 'ipv4' ? ` – ${num(2 ** (32 - bits))} Adressen` : ''}
              </option>
            ))}
          </Select>
          <Button
            variant="primary"
            onClick={() => void run({ type: 'ipam/buyBlock', family, length: Number(length) })}
          >
            Kaufen
          </Button>
        </div>
      </Card>

      <Card className="mt-3" title="Was hier nicht steht">
        <ul className="space-y-2 text-xs text-panel-600 dark:text-panel-300">
          <li>
            <strong>Standorte</strong> werden nicht gekauft, sondern gemietet und ausgehandelt – unter „Rechenzentren“.
            Dort liegen auch die Ausbaustufen für Strom, Kühlung und Notstrom.
          </li>
          <li>
            <strong>Transit und Glasfaser</strong> brauchen einen Anbieter und einen freien Router-Port. Das steht unter
            „Leitungen“, wo auch die laufenden Verträge sichtbar sind.
          </li>
          <li>
            <strong>Gebrauchte Hardware</strong> gibt es nicht. Was hier steht, ist neu und kommt mit voller Lebensdauer.
          </li>
        </ul>
      </Card>
    </>
  );
}
